<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.addGiftCards')"
    :info="$t('components.order.actions.addGiftCards.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('giftCard.new'), type: 'confirm', variant: 'white', validate: false, flat: true, outline: true, callback: handleCreateGiftCard },
      { text: $t('components.order.actions.addGiftCards.confirm'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          :info="$t('components.order.actions.addGiftCards.customerInfo')"
          class="mb-4 block"
          rules="required"
        >
          <customer-select v-model="customer" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="customer"
          v-slot="{ status }"
          :label="$t('general.giftCard')"
          :name="$t('general.giftCard')"
          class="block"
          rules="required"
        >
          <div class="block border rounded-lg bg-gray-100 overflow-hidden dark:bg-gray-900">
            <div v-if="!hasGiftCards && !loadingGiftCards" class="flex flex-col items-center justify-center mt-6 mb-6">
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addGiftCards.noUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <div v-if="loadingGiftCards" class="flex items-center justify-center gap-2 mt-6 mb-6" role="status">
              <o-loader size="20px" />
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addGiftCards.loadingUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <checkbox-group-field
              v-if="hasGiftCards && !loadingGiftCards"
              v-model="selected"
              :options="giftCardOptions"
              :status="status"
              search
              class="h-80"
            >
              <template #option="{ option }">
                <div class="w-full ml-3 flex justify-between text-gray-800 dark:text-gray-100 pt-1">
                  <div>
                    <p>{{ option.value.displayNumber }}</p>
                    <p v-if="option.value.customerEmailFromName" class="text-gray-600 dark:text-gray-300">
                      {{ option.value.customerEmailFromName }}
                    </p>
                  </div>
                  <p class="font-medium">
                    {{ option.value.balanceLabel }}
                  </p>
                </div>
              </template>
            </checkbox-group-field>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { Customer } from '~/_api/models/customer'

import CustomerSelect from '@/components/customer/elements/Select.vue'
import {
  CheckboxGroupField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
  (e: 'create'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const dialog = ref<any>()
const form = ref<any>()

const selected = ref<any[]>([])
const giftCards = ref<any>(null)
const customer = ref<any>(null)
const loadingGiftCards = ref(true)
let latestGiftCardsRequest = 0

const giftCardOptions = computed(() => {
  const cards = giftCards.value

  if (!cards) { return [] }

  return cards
    .filter((giftCard: any) => !giftCard.order)
    .map((giftCard: any) => ({
      value: giftCard,
      text: giftCard.number,
      info: giftCard.customNumber,
    }))
})

const hasGiftCards = computed(() => giftCards.value && !!giftCardOptions.value.length)

watch(customer, () => {
  fetchGiftCards()
})

function open (prefillCustomer = true) {
  if (prefillCustomer) {
    customer.value = new Customer({ ...props.order.customer })
  }

  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const selectedVal = selected.value
    const response = await $api.order.addGiftCards(String(props.order.idOrder), selectedVal.map((gc: any) => String(gc.idGiftCard)))
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addGiftCards.success'),
      text: t('components.order.actions.addGiftCards.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return form.value?.validate()
}

async function fetchGiftCards () {
  const requestId = ++latestGiftCardsRequest
  selected.value = []

  const customerVal = customer.value

  if (customerVal) {
    loadingGiftCards.value = true

    try {
      const response = await $api.customer.giftCards(customerVal.objectID(), { unpaid: true })

      if (requestId !== latestGiftCardsRequest) return

      giftCards.value = response.items
    } catch (e) {
      if (requestId !== latestGiftCardsRequest) return

      console.log(e)
      giftCards.value = []
    } finally {
      if (requestId === latestGiftCardsRequest) {
        loadingGiftCards.value = false
      }
    }
  } else {
    giftCards.value = []
    loadingGiftCards.value = false
  }
}

function handleCreateGiftCard () {
  emit('create')
  dialog.value.close()
}

defineExpose({ open })
</script>
